import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaLayerGroup, FaTimes } from "react-icons/fa";
import { useCategories } from "../../contexts/CategoryContext";
import topBarStyles from "../../styles/components/layout/TopBar.module.css";
import styles from "../../styles/components/layout/NavigationBar.module.css";
import capitalizeFirst from "../../utils/capitalizeFirst";

const CategoryMegaMenu = ({ categories, onClose, className = "" }) => (
  <div className={`${styles.dropdownMenu} ${className}`.trim()}>
    {categories.map((cat) => {
      const hasSubcategories =
        cat.subcategories &&
        cat.subcategories.filter((sub) => sub.isActive !== false).length > 0;

      return (
        <div key={cat._id} className={styles.categoryGroup}>
          <Link
            to={`/category/${encodeURIComponent(cat._id)}`}
            className={styles.dropdownCategory}
            onClick={onClose}
          >
            {capitalizeFirst(cat.name)}
          </Link>
          {hasSubcategories && (
            <div className={styles.subcategoriesList}>
              {cat.subcategories
                .filter((sub) => sub.isActive !== false)
                .map((sub) => (
                  <Link
                    key={sub._id || sub.slug}
                    to={`/category/${encodeURIComponent(cat._id)}?sub=${encodeURIComponent(sub.slug)}`}
                    className={styles.dropdownSubcategory}
                    onClick={onClose}
                  >
                    {sub.name}
                  </Link>
                ))}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const useCategoryMenu = () => {
  const { categories } = useCategories();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const closeMenu = useCallback(() => setIsOpen(false), []);
  const toggleMenu = useCallback(() => setIsOpen((prev) => !prev), []);

  const goToAllCategories = () => {
    closeMenu();

    if (location.pathname === "/") {
      document.getElementById("categories")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      if (location.hash !== "#categories") {
        navigate("/#categories", { replace: true });
      }
      return;
    }

    navigate("/#categories");
  };

  return {
    categories,
    isOpen,
    closeMenu,
    toggleMenu,
    goToAllCategories,
  };
};

/** Categories mega-menu trigger for the desktop top strip. */
const StripCategoryNav = () => {
  const { categories, isOpen, closeMenu, toggleMenu, goToAllCategories } =
    useCategoryMenu();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeMenu]);

  return (
    <div className={styles.navMenuTrigger} ref={dropdownRef}>
      <button
        type="button"
        className={`${topBarStyles.stripNavButton} ${topBarStyles.stripNavLinkLabeled} ${
          isOpen ? topBarStyles.stripNavLinkActive : ""
        }`}
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Categories"
        title="Categories"
      >
        <FaLayerGroup size={20} aria-hidden="true" />
        <span className={topBarStyles.stripNavLabel}>Categories</span>
      </button>
      {isOpen && (
        <div className={styles.dropdownMenuPanel}>
          <div className={styles.dropdownMenuPanelHeader}>
            <button
              type="button"
              className={styles.dropdownMenuPanelLink}
              onClick={goToAllCategories}
            >
              View all categories
            </button>
          </div>
          {categories.length > 0 ? (
            <CategoryMegaMenu categories={categories} onClose={closeMenu} />
          ) : null}
        </div>
      )}
    </div>
  );
};

/** Hamburger that opens the categories list as a mobile drawer. */
const MobileCategoryNav = () => {
  const { categories, isOpen, closeMenu, toggleMenu, goToAllCategories } =
    useCategoryMenu();

  useEffect(() => {
    if (!isOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeMenu]);

  return (
    <>
      <button
        type="button"
        className={topBarStyles.mobileCategoryTrigger}
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Open categories menu"
        title="Categories"
      >
        <FaBars size={20} aria-hidden="true" />
      </button>
      {isOpen &&
        createPortal(
          <div className={styles.mobileDrawerRoot}>
            <div
              className={styles.mobileDrawerBackdrop}
              onClick={closeMenu}
              aria-hidden="true"
            />
            <div
              className={styles.mobileDrawerPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Categories"
            >
              <div className={styles.mobileDrawerHeader}>
                <h2 className={styles.mobileDrawerTitle}>Categories</h2>
                <button
                  type="button"
                  className={styles.mobileDrawerClose}
                  onClick={closeMenu}
                  aria-label="Close categories menu"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </div>
              <div className={styles.mobileDrawerAllRow}>
                <button
                  type="button"
                  className={styles.mobileDrawerAllLink}
                  onClick={goToAllCategories}
                >
                  View all categories
                </button>
              </div>
              <div className={styles.mobileDrawerBody}>
                {categories.length > 0 ? (
                  <CategoryMegaMenu
                    categories={categories}
                    onClose={closeMenu}
                    className={styles.mobileDrawerMenu}
                  />
                ) : (
                  <p className={styles.noCategories}>No categories yet.</p>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

const CategoryNav = ({ variant = "strip" }) =>
  variant === "mobile" ? <MobileCategoryNav /> : <StripCategoryNav />;

export default CategoryNav;
